import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface ProcessingProgress {
  phase?: string;
  current?: number;
  total?: number;
  message?: string;
}

export interface RowError {
  row: number;
  error: string;
}

export type IssueSeverity = 'error' | 'warning' | 'info';

export interface ProcessingIssue {
  severity: IssueSeverity;
  message: string;
  count?: number;
}

export interface IssueSummary {
  errors?: number;
  warnings?: number;
  info?: number;
  total_affected?: number;
  truncated?: boolean;
}

export interface BulkImportProgress {
  processing_status: string;
  processing_progress: ProcessingProgress | null;
  listings_created: number | null;
  listings_updated: number | null;
  listing_ids: string[] | null;
  processing_error: string | null;
  errors: RowError[] | null;
  issues?: ProcessingIssue[] | null;
  issue_summary?: IssueSummary | null;
}

interface UseBulkImportProgressReturn {
  progress: BulkImportProgress | null;
  isComplete: boolean;
  isError: boolean;
  reset: () => void;
}

export function useBulkImportProgress(uploadId: string | null): UseBulkImportProgressReturn {
  const [progress, setProgress] = useState<BulkImportProgress | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [isError, setIsError] = useState(false);

  const reset = useCallback(() => {
    setProgress(null);
    setIsComplete(false);
    setIsError(false);
  }, []);

  useEffect(() => {
    if (!uploadId) {
      return;
    }

    // Fetch status from edge function (which proxies to FastAPI)
    const fetchProgress = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('get-bulk-import-status', {
          body: { upload_id: uploadId }
        });

        if (error) {
          console.error('Error fetching bulk import progress:', error);
          return;
        }

        if (data) {
          const progressData: BulkImportProgress = {
            processing_status: data.processing_status || 'pending',
            processing_progress: data.processing_progress as ProcessingProgress | null,
            listings_created: data.listings_created,
            listings_updated: data.listings_updated,
            listing_ids: data.listing_ids,
            processing_error: data.processing_error,
            errors: data.errors || null,
            issues: (data.issues as ProcessingIssue[] | null) ?? null,
            issue_summary: (data.issue_summary as IssueSummary | null) ?? null,
          };
          
          setProgress(progressData);

          if (data.processing_status === 'completed') {
            setIsComplete(true);
          } else if (data.processing_status === 'failed') {
            setIsComplete(true);
            setIsError(true);
          }
        }
      } catch (err) {
        console.error('Error fetching bulk import progress:', err);
      }
    };

    // Fetch immediately
    fetchProgress();

    // Poll every 2 seconds
    const interval = setInterval(fetchProgress, 2000);

    return () => clearInterval(interval);
  }, [uploadId]);

  return { progress, isComplete, isError, reset };
}
